import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Pure mapping from the API's user rows to what the table renders.
 * Components only ever read `UserRowView`, never raw API fields.
 */

export const PLACEHOLDER = '—';

/** Avatar colour pairs from the design, picked deterministically per userId. */
const AVATAR_STYLES = [
  'bg-primary-container text-on-primary',
  'bg-primary-fixed-dim text-on-primary-fixed',
  'bg-tertiary-fixed text-on-tertiary-fixed',
  'bg-surface-container-high text-on-surface-variant',
  'bg-secondary-fixed-dim text-on-primary-fixed',
  'bg-primary text-on-primary',
];

/** Fuel pill copy and styling. `highlighted` pills use the mint background from the design. */
const FUELS = {
  benzine: { label: 'Benzine', highlighted: false, dotClass: 'bg-secondary' },
  diesel: { label: 'Diesel', highlighted: false, dotClass: 'bg-outline' },
  electric: { label: 'Electric (EV)', highlighted: true, dotClass: 'bg-primary' },
  hybrid: { label: 'Hybrid', highlighted: true, dotClass: 'bg-secondary' },
  lpg: { label: 'LPG Autogas', highlighted: false, dotClass: 'bg-primary-container' },
};

/** "Daan van den Berg" → "DV", "dan" → "D", "" / null → "?". */
export function getInitials(name) {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0].toUpperCase())
    .join('');
}

/** Stable small hash (djb2) so the same user always gets the same colour. */
export function getAvatarClass(userId) {
  const key = String(userId ?? '');
  let hash = 5381;
  for (let i = 0; i < key.length; i += 1) hash = ((hash << 5) + hash + key.charCodeAt(i)) >>> 0;
  return AVATAR_STYLES[hash % AVATAR_STYLES.length];
}

/**
 * International format when the number is valid; otherwise the raw value.
 * Numbers without "+" are tried with one prefixed ("31612345678" → "+31 6 12345678").
 * Never throws.
 */
export function formatPhone(raw) {
  if (raw == null) return PLACEHOLDER;
  const value = String(raw).trim();
  if (!value) return PLACEHOLDER;
  try {
    const candidate = value.startsWith('+') ? value : `+${value.replace(/[^\d]/g, '')}`;
    const phone = parsePhoneNumberFromString(candidate);
    if (phone?.isValid()) return phone.formatInternational();
  } catch {
    // fall through to the raw value
  }
  return value;
}

/** @returns {{ key: string, label: string, highlighted: boolean, dotClass: string } | null} */
export function getFuel(fuelType) {
  if (typeof fuelType !== 'string') return null;
  const key = fuelType.trim().toLowerCase();
  return FUELS[key] ? { key, ...FUELS[key] } : null;
}

/**
 * @typedef {object} UserRowView
 * @property {string} key             React key
 * @property {number} position        "#" column
 * @property {string|null} userId     null when the row's userId was malformed
 * @property {string} name
 * @property {string} initials
 * @property {string} avatarClass
 * @property {string} subtitle        "POM95552 · Utrecht" or just the userId
 * @property {string|null} email
 * @property {boolean} emailVerified
 * @property {string} phone           formatted, raw, or "—"
 * @property {ReturnType<typeof getFuel>} fuel
 * @property {string} scansLabel      "12 scans" or "—"
 * @property {number|null} scansToNextTier
 * @property {string|null} tier
 * @property {number|null} couponsEarned
 * @property {'active'|'inactive'|'unknown'} status
 */

/**
 * @param {import('./userSchema').UserDto} user
 * @param {number} rowIndex  0-based index within the current page
 * @param {{ page: number, limit: number }} paging
 * @returns {UserRowView}
 */
export function toUserRowView(user, rowIndex, { page, limit }) {
  const name = user.name?.trim() || PLACEHOLDER;
  const city = user.city?.trim();
  const userId = user.userId ?? null;

  return {
    key: String(user.id ?? userId ?? `row-${rowIndex}`),
    position: (page - 1) * limit + rowIndex + 1,
    userId,
    name,
    initials: getInitials(user.name),
    avatarClass: getAvatarClass(userId ?? user.id),
    subtitle: [userId ?? PLACEHOLDER, city].filter(Boolean).join(' · '),
    email: user.email?.trim() || null,
    emailVerified: user.emailVerified === true,
    phone: formatPhone(user.phoneNumber),
    fuel: getFuel(user.fuelType),
    scansLabel: typeof user.scansMade === 'number' ? `${user.scansMade} ${user.scansMade === 1 ? 'scan' : 'scans'}` : PLACEHOLDER,
    scansToNextTier: user.scansToNextTier ?? null,
    tier: user.tier ?? null,
    couponsEarned: user.couponsEarned ?? null,
    status: user.isActive === true ? 'active' : user.isActive === false ? 'inactive' : 'unknown',
  };
}

export function toUserRowViews(users, paging) {
  return users.map((user, i) => toUserRowView(user, i, paging));
}
